/* eslint-disable */

const chrome = window.chrome;

function loadScript() {
  return new Promise((resolve, reject) => {
    window['__onGCastApiAvailable'] = resolve;

    if (window.chrome && !window.chrome.cast) {
      const script = document.createElement('script');
      script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
      script.onerror = reject;
      document.head.appendChild(script);
    } else {
      resolve();
    }
  });
}

class Castjs {
  constructor(opt = {}) {
    if (!opt.receiver || opt.receiver === '') {
      opt.receiver = 'CC1AD845';
    }

    // Private
    this._events = {};
    this._player = null;
    this._controller = null;
    this._mediaSession = null;
    this._timePollingInterval = null;
    this._lastPolledTime = 0;

    // Public
    this.version = 'v4.3.0';
    this.receiver = opt.receiver;
    this.joinpolicy = opt.joinpolicy;
    this.isNative = !!opt.isNative;

    this.available = false;
    this.connected = false;
    this.device = 'Chromecast';

    this.src = '';
    this.title = '';
    this.description = '';
    this.poster = '';
    this.subtitles = [];
    this.volumeLevel = 1;
    this.muted = false;
    this.paused = false;
    this.time = 0;
    this.timePretty = '00:00:00';
    this.duration = 0;
    this.durationPretty = '00:00:00';
    this.progress = 0;
    this.state = 'disconnected';

    this._init();
  }

  async _init() {
    try {
      if (!this.isNative) {
        if (!window.chrome?.cast?.isAvailable) {
          await Promise.race([loadScript(), new Promise(r => setTimeout(r, 5000))]);
        }
        if (!window.chrome?.cast?.isAvailable) {
          return this.trigger('error', 'Chromecast API not available');
        }
      }

      const { CastContext, RemotePlayer, RemotePlayerController, CastState, CastContextEventType, RemotePlayerEventType } = cast.framework;

      const validPolicies = [
        chrome.cast.AutoJoinPolicy.TAB_AND_ORIGIN_SCOPED,
        chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        chrome.cast.AutoJoinPolicy.PAGE_SCOPED
      ];

      if (!validPolicies.includes(this.joinpolicy)) {
        this.joinpolicy = chrome.cast.AutoJoinPolicy.TAB_AND_ORIGIN_SCOPED;
      }

      CastContext.getInstance().setOptions({
        receiverApplicationId: this.receiver,
        autoJoinPolicy: this.joinpolicy,
        language: 'en-US',
        resumeSavedSession: false,
      });

      this._player = new RemotePlayer();
      this._controller = new RemotePlayerController(this._player);

      this._registerListeners(CastContext, CastContextEventType, RemotePlayerEventType);

      this.available = CastContext.getInstance().getCastState() !== CastState.NO_DEVICES_AVAILABLE;
      this.trigger('available');

    } catch (error) {
      this.trigger('error', error.message || error);
    }
  }

  _registerListeners(CastContext, CastContextEventType, RemotePlayerEventType) {
    // Core player events
    this._controller.addEventListener(RemotePlayerEventType.IS_CONNECTED_CHANGED, this._isConnectedChanged.bind(this));
    this._controller.addEventListener(RemotePlayerEventType.IS_MEDIA_LOADED_CHANGED, this._isMediaLoadedChanged.bind(this));
    this._controller.addEventListener(RemotePlayerEventType.IS_MUTED_CHANGED, this._isMutedChanged.bind(this));
    this._controller.addEventListener(RemotePlayerEventType.IS_PAUSED_CHANGED, this._isPausedChanged.bind(this));
    this._controller.addEventListener(RemotePlayerEventType.DURATION_CHANGED, this._durationChanged.bind(this));
    this._controller.addEventListener(RemotePlayerEventType.VOLUME_LEVEL_CHANGED, this._volumeLevelChanged.bind(this));
    this._controller.addEventListener(RemotePlayerEventType.PLAYER_STATE_CHANGED, this._playerStateChanged.bind(this));

    // Time events - primary + fallback
    this._controller.addEventListener(RemotePlayerEventType.CURRENT_TIME_CHANGED, this._currentTimeChanged.bind(this));

    CastContext.getInstance().addEventListener(
      CastContextEventType.CAST_STATE_CHANGED,
      this._isAvailableChanged.bind(this)
    );
  }

  // ==================== TIME UPDATES (Improved) ====================
  _startTimePolling() {
    this._stopTimePolling();
    this._timePollingInterval = setInterval(() => {
      console.log("polling interval 1");
      if (!this.connected || !this._player?.isMediaLoaded || this.paused) return;

      try {
        console.log("polling interval 2");
        const session = cast.framework.CastContext.getInstance().getCurrentSession();
        const estimatedTime = session?.getMediaSession()?.getEstimatedTime() ?? this._player.currentTime;

        if (Math.abs(estimatedTime - this._lastPolledTime) >= 0.25) {
          console.log("polling interval 3");
          this._lastPolledTime = estimatedTime;
          this._updateTime(estimatedTime);
        }
      } catch (e) {
        console.log("polling interval 4", e);
      }
    }, 600); // Balanced interval
  }

  _stopTimePolling() {
    if (this._timePollingInterval) {
      clearInterval(this._timePollingInterval);
      this._timePollingInterval = null;
    }
    this._lastPolledTime = 0;
  }

  _currentTimeChanged() {
    if (this._player.isMediaLoaded) {
      this._updateTime(this._player.currentTime);
    }
  }

  _updateTime(currentTime) {
    this.time = Math.max(0, Math.round(currentTime * 10) / 10);
    this.timePretty = this._controller.getFormattedTime(this.time);
    this.progress = this.duration > 0 ? Math.min(100, (this.time / this.duration) * 100) : 0;
    this.trigger('timeupdate');
  }

  // ==================== OTHER EVENT HANDLERS ====================
  _isAvailableChanged() {
    const { CastState } = cast.framework;
    this.available = cast.framework.CastContext.getInstance().getCastState() !== CastState.NO_DEVICES_AVAILABLE;
    this.trigger('available');
  }

  _isConnectedChanged() {
    this.connected = this._player.isConnected;
    this.state = this.connected ? 'connected' : 'disconnected';

    if (this.connected) {
      this.device = cast.framework.CastContext.getInstance().getCurrentSession()?.getCastDevice()?.friendlyName || this.device;
      this._startTimePolling();
    } else {
      this._stopTimePolling();
    }

    this.trigger('statechange');
    this.trigger(this.connected ? 'connect' : 'disconnect');
  }

  _isMediaLoadedChanged() {
    if (!this._player.isMediaLoaded) return;
    setTimeout(() => this._updateMediaInfo(), 30);
  }

  _updateMediaInfo() {
    const session = cast.framework.CastContext.getInstance().getCurrentSession();
    if (!session || !this._player.mediaInfo) return;

    this.device = session.getCastDevice()?.friendlyName || this.device;
    this.src = this._player.mediaInfo.contentId || '';
    this.title = this._player.mediaInfo.metadata?.title || '';
    this.description = this._player.mediaInfo.metadata?.subtitle || '';
    this.poster = this._player.imageUrl || '';
    this.volumeLevel = Number(this._player.volumeLevel.toFixed(1));
    this.muted = this._player.isMuted;
    this.paused = this._player.isPaused;
    this.duration = this._player.duration || 0;
    this.durationPretty = this._controller.getFormattedTime(this.duration);

    this._updateTime(this._player.currentTime);
    this._updateSubtitles();
  }

  _updateSubtitles() {
    this.subtitles = [];
    const mediaInfo = this._player.mediaInfo;
    if (!mediaInfo?.tracks) return;

    mediaInfo.tracks.forEach((track, index) => {
      if (track.type === chrome.cast.media.TrackType.TEXT) {
        this.subtitles.push({
          label: track.name || `Subtitle ${index + 1}`,
          src: track.trackContentId,
          active: false
        });
      }
    });

    const activeIds = session?.getMediaSession()?.activeTrackIds;
    if (activeIds?.length) {
      const idx = this.subtitles.findIndex((_, i) => i === activeIds[0]);
      if (idx !== -1) this.subtitles[idx].active = true;
    }
  }

  _isMutedChanged() {
    const wasMuted = this.muted;
    this.muted = this._player.isMuted;
    if (wasMuted !== this.muted) this.trigger(this.muted ? 'mute' : 'unmute');
  }

  _isPausedChanged() {
    console.log("x chromecast", this._player.isPaused);
    this.paused = this._player.isPaused;
    if (this.paused) this.trigger('pause');
  }

  _durationChanged() {
    this.duration = this._player.duration || 0;
    this.durationPretty = this._controller.getFormattedTime(this.duration);
  }

  _volumeLevelChanged() {
    this.volumeLevel = Number(this._player.volumeLevel.toFixed(1));
    if (this._player.isMediaLoaded) this.trigger('volumechange');
  }

  _playerStateChanged() {
    if (!this.connected) return;

    this.state = this._player.playerState.toLowerCase();

    switch (this.state) {
      case 'idle':
        const idleReason = this._player.idleReason;
        this.trigger('statechange');
        if (idleReason !== chrome.cast.media.IdleReason.INTERRUPTED) {
          this.state = 'ended';
          this.trigger('end');
        }
        break;

      case 'buffering':
        this._updateTime(this._player.currentTime);
        this.trigger('statechange');
        this.trigger('buffering');
        break;

      case 'playing':
        setTimeout(() => {
          this.trigger('statechange');
          this.trigger('playing');
        }, 10);
        break;

      case 'paused':
        this.trigger('statechange');
        this.trigger('pause');
        break;
    }
  }

  // ==================== PUBLIC API ====================
  on(event, cb) {
    if (!this._events[event]) this._events[event] = [];
    this._events[event].push(cb);
    return this;
  }

  off(event, fn) {
    if (!event) {
      this._events = {};
    } else if (this._events[event]) {
      if (fn) {
        this._events[event] = this._events[event].filter(f => f !== fn);
      } else {
        delete this._events[event];
      }
    }
    return this;
  }

  trigger(event, ...args) {
    if (this._events[event]) {
      this._events[event].forEach(cb => cb.apply(this, args));
    }
    if (event !== 'error' && this._events.event) {
      this._events.event.forEach(cb => cb.call(this, event, ...args));
    }
    return this;
  }

  async requestSession() {
    try {
      const { CastContext } = cast.framework;
      if (!CastContext.getInstance().getCurrentSession()) {
        await CastContext.getInstance().requestSession();
      }
    } catch (error) {
      if (error?.code !== 'cancel') this.trigger('error', error);
    }
  }

  async cast(src, metadata = {}, autoplay = true) {
    if (!src) return this.trigger('error', 'No media source specified');

    this.src = src;
    Object.assign(this, metadata);

    try {
      const { CastContext } = cast.framework;
      if (!CastContext.getInstance().getCurrentSession()) {
        await CastContext.getInstance().requestSession();
      }
      await this._loadMedia(autoplay);
    } catch (error) {
      if (error?.code !== 'cancel') this.trigger('error', error);
    }
  }

  async _loadMedia(autoplay) {
    const session = cast.framework.CastContext.getInstance().getCurrentSession();
    if (!session) throw new Error('No active session');

    const mediaInfo = new chrome.cast.media.MediaInfo(this.src);
    mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();

    mediaInfo.metadata.title = this.title;
    mediaInfo.metadata.subtitle = this.description;
    if (this.poster) {
      mediaInfo.metadata.images = [new chrome.cast.Image(this.poster)];
    }

    if (this.subtitles?.length) {
      mediaInfo.textTrackStyle = new chrome.cast.media.TextTrackStyle();
      mediaInfo.textTrackStyle.backgroundColor = '#00000000';
      mediaInfo.textTrackStyle.edgeType = chrome.cast.media.TextTrackEdgeType.DROP_SHADOW;
      mediaInfo.textTrackStyle.fontFamily = chrome.cast.media.TextTrackFontGenericFamily.CASUAL;
      mediaInfo.textTrackStyle.fontScale = 1.0;
      mediaInfo.textTrackStyle.foregroundColor = '#FFFFFF';

      mediaInfo.tracks = this.subtitles.map((sub, i) => {
        const track = new chrome.cast.media.Track(i, chrome.cast.media.TrackType.TEXT);
        track.name = sub.label;
        track.subtype = chrome.cast.media.TextTrackType.CAPTIONS;
        track.trackContentId = sub.src;
        track.trackContentType = 'text/vtt';
        track.trackId = i;
        return track;
      });
    }

    const request = new chrome.cast.media.LoadRequest(mediaInfo);
    request.currentTime = 0;
    this._updateTime(0);
    request.autoplay = autoplay;

    const activeIdx = this.subtitles.findIndex(s => s.active);
    if (activeIdx !== -1) request.activeTrackIds = [activeIdx];

    await session.loadMedia(request);
    this._mediaSession = session.getMediaSession();
  }

  seek(seconds, isPercentage = false) {
    console.log("x chromecast seek", seconds);
    if (isPercentage && this.duration) {
      seconds = (seconds / 100) * this.duration;
    }
    this._player.currentTime = Math.max(0, seconds);
    this._controller.seek();
    return this;
  }

  volume(level) {
    this._player.volumeLevel = Math.max(0, Math.min(1, level));
    this._controller.setVolumeLevel();
    return this;
  }

  play() {
    if (this.paused) {
      this._controller.playOrPause();
      return this;
    }
  }
  pause() {
    if (!this.paused) {
      this._controller.playOrPause();
      return this;
    }
  }
  stop() { this._controller.stop(); return this; }
  mute() { this._controller.muteOrUnmute(); return this; }
  unmute() { this._controller.muteOrUnmute(); return this; }

  subtitle(index) {
    if (!this.subtitles[index]) return this;
    const request = new chrome.cast.media.EditTracksInfoRequest([parseInt(index)]);
    const session = cast.framework.CastContext.getInstance().getCurrentSession();

    session.getMediaSession().editTracksInfo(request, () => {
      this.subtitles.forEach((s, i) => { s.active = (i === parseInt(index)); });
      this.trigger('subtitlechange');
    }, err => this.trigger('error', err));
  }

  disconnect() {
    this._stopTimePolling();
    try {
      cast.framework.CastContext.getInstance().endCurrentSession(true);
    } catch (e) { }
    this._cleanup();
    this.trigger('disconnect');
    return this;
  }

  _cleanup() {
    this.connected = false;
    this.state = 'disconnected';
    this.device = 'Chromecast';
    this.src = this.title = this.description = this.poster = '';
    this.subtitles = [];
    this.time = this.duration = this.progress = 0;
    this.timePretty = this.durationPretty = '00:00:00';
    this.volumeLevel = 1;
    this.muted = this.paused = false;
  }

  destroy() {
    this._stopTimePolling();
    this.off();
    this.disconnect();
  }
}

const Cast = Castjs;
export default Cast;